import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';
import { fetchSapJobRunDetail } from '@/lib/sapJobsApi';
import { formatDurationSeconds, formatIsoDateTime } from '@/lib/sapJobStatus';
import SapJobStatusBadge from '@/components/sapJobs/SapJobStatusBadge';
import SapJobLogPanel from '@/components/sapJobs/SapJobLogPanel';
import SapJobOwnershipPanel from '@/components/sapJobs/SapJobOwnershipPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function decodeRunParam(raw) {
  try {
    return decodeURIComponent(raw || '');
  } catch {
    return raw || '';
  }
}

function failureMessageText(failureMessage) {
  if (!failureMessage) return null;
  if (typeof failureMessage === 'string') return failureMessage;
  return failureMessage.text || failureMessage.message || JSON.stringify(failureMessage);
}

export default function SapJobRunDetail() {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('dashboard', 'read');
  const navigate = useNavigate();
  const params = useParams();
  const runId = decodeRunParam(params['*']);

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canView || !runId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSapJobRunDetail(runId);
      setRun(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load run');
      setRun(null);
    } finally {
      setLoading(false);
    }
  }, [canView, runId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canView) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          You do not have permission to view this run.
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading run…
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="space-y-4" data-testid="sap-job-run-error">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-destructive">{error || 'Run not found'}</CardContent>
        </Card>
      </div>
    );
  }

  const isCanceled = String(run.status || '').toUpperCase() === 'A';
  const isFinished = String(run.status || '').toUpperCase() === 'F';
  const failText = failureMessageText(run.failure_message);

  return (
    <div className="space-y-6" data-testid="sap-job-run-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      <div>
        <h1 className="text-2xl font-semibold">{run.job_name}</h1>
        <p className="text-sm text-muted-foreground font-mono break-all mt-1">{run.run_id}</p>
        <div className="mt-3">
          <SapJobStatusBadge sapStatus={run.status} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Run summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2 text-sm">
          <div>System / client: {run.sap_system} / {run.sap_client}</div>
          <div>Job count: {run.job_count}</div>
          <div>Planned start: {formatIsoDateTime(run.planned_start)}</div>
          <div>Actual start: {formatIsoDateTime(run.actual_start)}</div>
          <div>Actual end: {formatIsoDateTime(run.actual_end)}</div>
          <div>Duration: {formatDurationSeconds(run.duration_seconds)}</div>
          <div>Program: <span className="font-mono">{run.program || '—'}</span></div>
          <div>Variant: <span className="font-mono">{run.variant || '—'}</span></div>
          <div>Evidence status: {run.evidence_status || '—'}</div>
          <div>Evidence fetched: {formatIsoDateTime(run.evidence_fetched_at)}</div>
        </CardContent>
      </Card>

      <SapJobOwnershipPanel ownership={run.ownership} />

      <Card data-testid="sap-job-run-evidence">
        <CardHeader>
          <CardTitle>Failure evidence</CardTitle>
          <CardDescription>
            Log lines from SAP when a run was canceled; successful runs show a neutral message.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isFinished && !failText && !(run.log_lines || []).length ? (
            <p className="text-muted-foreground text-sm" data-testid="run-success-no-evidence">
              No failure evidence — run completed successfully.
            </p>
          ) : null}

          {isCanceled || failText ? (
            <div>
              <p className="text-sm font-medium mb-1">Failure message</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{failText || '—'}</p>
            </div>
          ) : null}

          {!isCanceled && !isFinished && !failText && !(run.log_lines || []).length ? (
            <p className="text-muted-foreground text-sm">No failure evidence for this run.</p>
          ) : null}

          <div>
            <p className="text-sm font-medium mb-2">SAP job log</p>
            <SapJobLogPanel
              lines={run.log_lines}
              emptyMessage={
                isFinished
                  ? 'No failure evidence — run completed successfully.'
                  : 'No log lines returned for this run.'
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
